import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Stethoscope, LogOut, Save, Plus, Trash2, Eye } from "lucide-react";

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("doctorToken");
      const response = await axios.get(`${API}/doctor/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Eroare la încărcarea profilului");
      if (error.response?.status === 401) {
        localStorage.removeItem("doctorToken");
        navigate("/doctor/auth");
      }
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("doctorToken");
      const { id, email, created_at, ...updateData } = profile;
      
      const response = await axios.put(`${API}/doctor/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setProfile(response.data);
      toast.success("Profil actualizat cu succes!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizarea profilului");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("doctorToken");
    toast.success("Deconectare reușită");
    navigate("/");
  };

  const addWorkplace = () => {
    const workplaces = profile?.workplaces || [];
    if (workplaces.length >= 5) {
      toast.error("Poți adăuga maximum 5 locuri de muncă");
      return;
    }
    setProfile({
      ...profile,
      workplaces: [...workplaces, { type: "", name: "", phone: "", schedule: "" }]
    });
  };

  const removeWorkplace = (index) => {
    const workplaces = [...(profile?.workplaces || [])];
    workplaces.splice(index, 1);
    setProfile({ ...profile, workplaces });
  };

  const updateWorkplace = (index, field, value) => {
    const workplaces = [...(profile?.workplaces || [])];
    workplaces[index] = { ...workplaces[index], [field]: value };
    setProfile({ ...profile, workplaces });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-teal-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Se încarcă profilul...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-teal-100">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-8 h-8 text-teal-600" />
            <h1 className="text-2xl font-bold text-gray-800">Panou DocPlan</h1>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="view-public-profile-btn"
              onClick={() => navigate("/")}
              variant="outline"
              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              Vezi Pagina Publică
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Card className="max-w-4xl mx-auto p-8 bg-white/80 backdrop-blur-md border border-gray-200 shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Editează-ți Profilul</h2>
            <div className="flex items-center gap-2 bg-teal-50 px-4 py-2 rounded-full border border-teal-200">
              <Eye className="w-5 h-5 text-teal-600" />
              <span className="text-sm font-medium text-teal-700">{profile?.views || 0} vizualizări</span>
            </div>
          </div>
          <form onSubmit={handleSave} className="space-y-6" data-testid="profile-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Nume *</Label>
                <Input
                  data-testid="profile-name-input"
                  id="name"
                  value={profile?.name || ""}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="specialty">Specialitate *</Label>
                <Input
                  data-testid="profile-specialty-input"
                  id="specialty"
                  value={profile?.specialty || ""}
                  onChange={(e) => setProfile({ ...profile, specialty: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="professional_title">Grad Profesional *</Label>
                <Input
                  data-testid="profile-title-input"
                  id="professional_title"
                  value={profile?.professional_title || ""}
                  onChange={(e) => setProfile({ ...profile, professional_title: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="competencies">Competențe</Label>
              <Textarea
                data-testid="profile-competencies-input"
                id="competencies"
                value={profile?.competencies || ""}
                onChange={(e) => setProfile({ ...profile, competencies: e.target.value })}
                placeholder="Enumeră domeniile tale de expertiză și competențe"
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Workplaces Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Locuri de Muncă</h3>
                <Button
                  data-testid="add-workplace-btn"
                  type="button"
                  onClick={addWorkplace}
                  disabled={(profile?.workplaces || []).length >= 5}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adaugă Loc de Muncă
                </Button>
              </div>
              
              {(profile?.workplaces || []).length === 0 ? (
                <p className="text-gray-500 text-sm mb-4">Niciun loc de muncă adăugat. Apasă butonul de mai sus pentru a adăuga.</p>
              ) : (
                <div className="space-y-4">
                  {(profile?.workplaces || []).map((workplace, index) => (
                    <Card key={index} className="p-4 bg-gray-50 border border-gray-200">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor={`workplace-type-${index}`}>Tip Loc de Muncă *</Label>
                              <Select
                                value={workplace.type}
                                onValueChange={(value) => updateWorkplace(index, "type", value)}
                              >
                                <SelectTrigger data-testid={`workplace-type-${index}`} className="mt-1">
                                  <SelectValue placeholder="Selectează tipul" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Cabinet privat">Cabinet privat</SelectItem>
                                  <SelectItem value="Spital de stat">Spital de stat</SelectItem>
                                  <SelectItem value="Clinică privată">Clinică privată</SelectItem>
                                  <SelectItem value="Policlinică">Policlinică</SelectItem>
                                  <SelectItem value="Centru medical">Centru medical</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor={`workplace-phone-${index}`}>Număr de Telefon *</Label>
                              <Input
                                data-testid={`workplace-phone-${index}`}
                                id={`workplace-phone-${index}`}
                                type="tel"
                                value={workplace.phone}
                                onChange={(e) => updateWorkplace(index, "phone", e.target.value)}
                                placeholder="+40 123 456 789"
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor={`workplace-name-${index}`}>Nume Loc de Muncă *</Label>
                            <Input
                              data-testid={`workplace-name-${index}`}
                              id={`workplace-name-${index}`}
                              type="text"
                              value={workplace.name}
                              onChange={(e) => updateWorkplace(index, "name", e.target.value)}
                              placeholder="ex. Spitalul Universitar București, Cabinet Dr. Popescu"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`workplace-schedule-${index}`}>Orar</Label>
                            <Textarea
                              data-testid={`workplace-schedule-${index}`}
                              id={`workplace-schedule-${index}`}
                              value={workplace.schedule || ""}
                              onChange={(e) => updateWorkplace(index, "schedule", e.target.value)}
                              placeholder="ex. Luni-Vineri: 9:00-17:00, Sâmbătă: 9:00-13:00"
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <Button
                          data-testid={`remove-workplace-${index}`}
                          type="button"
                          onClick={() => removeWorkplace(index)}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 mt-6"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Date de Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="whatsapp">Număr WhatsApp</Label>
                  <Input
                    data-testid="profile-whatsapp-input"
                    id="whatsapp"
                    type="tel"
                    value={profile?.whatsapp || ""}
                    onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })}
                    placeholder="+40 123 456 789"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="facebook">Profil Facebook</Label>
                  <Input
                    data-testid="profile-facebook-input"
                    id="facebook"
                    type="url"
                    value={profile?.facebook || ""}
                    onChange={(e) => setProfile({ ...profile, facebook: e.target.value })}
                    placeholder="https://facebook.com/profilultau"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram">Profil Instagram</Label>
                  <Input
                    data-testid="profile-instagram-input"
                    id="instagram"
                    type="url"
                    value={profile?.instagram || ""}
                    onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                    placeholder="https://instagram.com/profilultau"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="other_contacts">Alte Date de Contact</Label>
              <Textarea
                data-testid="profile-other-contacts-input"
                id="other_contacts"
                value={profile?.other_contacts || ""}
                onChange={(e) => setProfile({ ...profile, other_contacts: e.target.value })}
                placeholder="Alte modalități prin care pacienții te pot contacta"
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Locație (Opțional)</h3>
              <p className="text-sm text-gray-600 mb-4">Adaugă coordonatele pentru a apărea pe hartă</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="latitude">Latitudine</Label>
                  <Input
                    data-testid="profile-latitude-input"
                    id="latitude"
                    type="number"
                    step="any"
                    value={profile?.latitude || ""}
                    onChange={(e) => setProfile({ ...profile, latitude: parseFloat(e.target.value) || null })}
                    placeholder="45.9432"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitudine</Label>
                  <Input
                    data-testid="profile-longitude-input"
                    id="longitude"
                    type="number"
                    step="any"
                    value={profile?.longitude || ""}
                    onChange={(e) => setProfile({ ...profile, longitude: parseFloat(e.target.value) || null })}
                    placeholder="24.9668"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6">
              <Button
                data-testid="save-profile-btn"
                type="submit"
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 rounded-full h-11"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Se salvează..." : "Salvează Modificările"}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
